import { buildDateTimeFormats, datetimeFormats } from "@/i18n/datetimeFormats";
import { loadLocaleMessages } from "@/i18n/messages";
import { LocaleEnum } from "@/services/internalApiV1";
import { createI18n, type I18n } from "vue-i18n";

let _i18n: I18n | undefined;
let _lastTimezone: string | null = null;
const supportedLocales = Object.values(LocaleEnum) as LocaleEnum[];

const normalizeLocale = (
  locale: string | null | undefined
): LocaleEnum | null => {
  if (!locale) return null;

  const normalizedLocale = locale.split("-")[0].toLowerCase() as LocaleEnum;
  return supportedLocales.includes(normalizedLocale) ? normalizedLocale : null;
};

const resolveInitialLocale = (): LocaleEnum => {
  const searchParams = new URLSearchParams(window.location.search);
  const localeFromParams = normalizeLocale(searchParams.get("locale"));
  const localeFromDocument = normalizeLocale(document.documentElement.lang);
  const localeFromBrowser = navigator.languages
    .map((locale) => normalizeLocale(locale))
    .find((locale): locale is LocaleEnum => locale !== null);

  return (
    localeFromParams ?? localeFromDocument ?? localeFromBrowser ?? LocaleEnum.DE
  );
};

export const generateI18n = (): I18n => {
  const messages = loadLocaleMessages();

  _i18n = createI18n({
    legacy: false,
    fallbackLocale: LocaleEnum.DE,
    availableLocales: Object.values(LocaleEnum),
    warnHtmlMessage: false,
    locale: resolveInitialLocale(),
    messages,
    datetimeFormats,
  });

  return _i18n;
};

// Translate outside of a Vue component setup (e.g. axios interceptors), where
// `useI18n()` is unavailable. Falls back to the key if i18n is not yet initialised.
export const translate = (
  key: string,
  named?: Record<string, unknown>,
  plural?: number
): string => {
  if (!_i18n) return key;
  const t = _i18n.global.t as (
    key: string,
    named?: Record<string, unknown>,
    plural?: number
  ) => string;
  if (named === undefined) return t(key);
  if (plural === undefined) return t(key, named);
  return t(key, named, plural);
};

export const setI18nTimezone = (timezone: string): void => {
  if (!_i18n) return;
  if (timezone === _lastTimezone) return;
  _lastTimezone = timezone;

  const formats = buildDateTimeFormats(timezone);
  const composer = _i18n.global;

  for (const locale of Object.keys(formats)) {
    composer.setDateTimeFormat(locale, formats[locale]);
  }
};
